
import React, { useState, useEffect } from 'react'

import _ from 'lodash'
import unflatten from '@stackhouse/dashboard-components/utils/unflatten'
import { withDashboardPageMode } from '@stackhouse/dashboard-components/Dashboard'
import { DashboardPageModeContextProvider } from '@stackhouse/dashboard-components/Dashboard/context'

const getActionIds = (arr = []) => _.uniq(arr.map(i => i.id).filter(Boolean))

const getPermissions = (body, functionName, functionRecordId, client) => {
    return client.post(`/permission/Functions/${functionName}${functionRecordId ? `/${functionRecordId}` : ''}`, body).then(({ data }) => data)
}

const getActionProps = (actions, permissions, editable) => {

    if (!permissions) return []

    return actions.reduce((acc, value) => {

        const { id } = value
        const actionValue = { ...value }

        if (actionValue.force) {
            return [...acc, actionValue]
        }

        if (actionValue.visible) {
            if (actionValue.visible.every(dep => permissions[dep] != -1)) {
                return [...acc, actionValue]
            }
            return acc
        }


        if (!permissions[id]) return acc
        if (permissions[id] == -1) return acc

        if (permissions[id] == 0) actionValue.disabled = true
        if (permissions[id] == 1) actionValue.disabled = false

        if (permissions[id] == 2) actionValue.disabled = !editable
        if (id == 'edit') actionValue.disabled = editable

        return [...acc, actionValue]
    }, [])
}

const processActions = (actions, permissions, editable) => {

    const { actions: actionsActions = {}, children: childrenActions = {} } = actions || {}
    const { actions: actionsPermissions = {}, children: childrenPermissions = {} } = permissions || {}

    const actionsProcessed = getActionProps(actionsActions, actionsPermissions, editable)
    const childrenProcessed = Object.entries(childrenActions).reduce(
        (acc, [key, value]) => ({ ...acc, [key]: getActionProps(value, childrenPermissions[key], editable) }),
        {})

    return { actions: actionsProcessed, children: childrenProcessed }
}

const withPermissions = (
    Component,
    functionName,
    idKey,
    actions = [],
    childrenActions = {},
    childrenList = [],
    options = {}
) => {

    return withDashboardPageMode(React.memo(React.forwardRef((props, ref) => {

        const { client, isCurrentRoute, setNavbarActions, withLoader } = props
        const { match: { params: { [idKey]: functionRecordId } = {} } = {} } = props

        const isIdZero = !+functionRecordId
        const { editable: initialEditable = isIdZero } = options

        const [canView, setCanView] = useState({})
        const [permissions, setPermissions] = useState(null)
        const [processedActions, setProcessedActions] = useState({})

        const [mode, setMode] = useState()
        const [editable, setEditable] = useState(initialEditable)

        useEffect(() => {
            if (isIdZero) {
                setMode('insert')
            }
            else if (editable) {
                setMode('edit')
            }
            else {
                setMode('view')
            }
        }, [isIdZero, editable])

        useEffect(() => {
            const actionsIds = getActionIds(actions)
            const childrenIds = childrenList.reduce((acc, child) => {
                return { ...acc, [child]: [...getActionIds(childrenActions[child]), 'view'] }
            }, {})

            withLoader(done => {
                getPermissions({
                    actions: actionsIds,
                    children: childrenIds
                },
                    functionName,
                    functionRecordId,
                    client
                )
                    .then(setPermissions)
                    .then(done)
                    .catch(done)
            })

        }, [])

        useEffect(() => {
            if (!isCurrentRoute) return

            const nextActions = processActions({ actions, children: childrenActions }, permissions, editable)
            const nextProcessedActions = {
                actions: unflatten(_.groupBy(nextActions.actions, 'container')),
                children: Object.entries(nextActions.children).reduce((acc, [key, value]) => ({ ...acc, [key]: unflatten(_.groupBy(value, 'container')) }), {}),
            }

            setProcessedActions(nextProcessedActions)
            setNavbarActions(nextProcessedActions.actions.navbar)

        }, [permissions, editable, isCurrentRoute])


        useEffect(() => {
            const { children = {} } = permissions || {}
            setCanView(childrenList.reduce((acc, key) => ({ ...acc, [key]: children[key] && children[key].view == 1 }), {}))
        }, [permissions])


        if (!permissions)
            return null

        return (
            <DashboardPageModeContextProvider value={mode}>
                <Component
                    ref={ref}
                    {...props}
                    editable={editable}
                    setEditable={setEditable}
                    view={canView}
                    actions={processedActions}
                />
            </DashboardPageModeContextProvider>
        )
    })))

}

export { withPermissions, withPermissions as default }